/**
 * claimWedding — "take this signed-in couple to their wedding, making one if
 * they have none."
 *
 * This used to live only inside LandingPage.handleCreate, and only ran on the
 * branch where the couple was *already* signed in. The signed-out branch minted
 * a wedding id before sending them to /couple-auth, so a returning couple on a
 * device with no localStorage hint — a new phone, a different browser, cleared
 * data — came back from sign-in holding a brand new id, landed on a document
 * that did not exist, and was walked through creating a second wedding. Their
 * real one was never looked for, because the lookup lived on the other branch.
 *
 * So the lookup moves here and both entry points go through it after auth has
 * settled. Nothing mints an id before we know who the couple is.
 */
import { doc, setDoc, getDocs, collection, query, where, serverTimestamp } from 'firebase/firestore';
import { db, generateWeddingId } from '@/lib/firebase';
import { consentRecord } from '@/lib/legalVersions';

/**
 * Advisory only. A cold Firestore connection on mobile data (common when an
 * installed PWA is launched from scratch) can leave this read pending
 * indefinitely, so it must never gate the outcome.
 */
const DUPLICATE_CHECK_TIMEOUT_MS = 8000;
/** Attempts at the ownership lookup before falling back. See the loop below. */
const LOOKUP_ATTEMPTS = 3;
const LOOKUP_RETRY_MS = 400;
const CREATE_TIMEOUT_MS = 20000;

const withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms)),
]);

/**
 * The couple's active wedding, or a new one.
 *
 * @param {import('firebase/auth').User} user  non-anonymous, auth already settled
 * @param {{ resumeHint?: string|null }} opts  a weddingId this device remembers,
 *        used only when the lookup could not complete — opening a remembered
 *        wedding is always better than risking a duplicate.
 * @returns {Promise<{ weddingId: string, outcome: 'existing'|'resume-hint'|'created' }>}
 */
export async function resolveOrCreateWedding(user, { resumeHint = null } = {}) {
  if (!user || user.isAnonymous) throw new Error('not-signed-in');

  let existing = null;
  // Retried, because creating is the consequence of failure here and we have
  // seen this lookup fail in production for a reason that could not be
  // reproduced afterwards — the couple was handed a duplicate wedding for it.
  // A transient rejection must not be enough to conclude "owns nothing".
  //
  // Only non-timeout failures are retried. A timeout already waited the full
  // budget, so trying twice more would leave the couple staring at a spinner
  // for three times as long to reach the same fallback.
  for (let attempt = 0; attempt < LOOKUP_ATTEMPTS && existing === null; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, LOOKUP_RETRY_MS * attempt));
    try {
      existing = await withTimeout(
        // Ownership only. `where('status', '!=', 'purged')` looks equivalent
        // and is not: Firestore's != excludes documents where the field is
        // ABSENT, so every wedding created before `status` existed was
        // invisible here — a couple whose wedding predates that field would be
        // told they have none and handed a new one. lifecycle.ts hit exactly
        // this and dropped the inequality for the same reason. Purged weddings
        // are filtered below instead, which also removes the composite-index
        // dependency this query used to carry.
        getDocs(query(
          collection(db, 'weddings'),
          where('ownerUids', 'array-contains', user.uid),
        )),
        DUPLICATE_CHECK_TIMEOUT_MS,
      );
    } catch (lookupErr) {
      // Logged as an error, not a warning: console filters routinely hide
      // warnings, and which of the two Firestore calls failed is the first
      // thing anyone debugging this needs to know.
      const code = lookupErr?.code || lookupErr?.message || 'unknown';
      const timedOut = lookupErr?.message === 'timeout';
      const last = timedOut || attempt === LOOKUP_ATTEMPTS - 1;
      console.error(
        `Wedding claim: lookup failed [${code}] on attempt ${attempt + 1}/${LOOKUP_ATTEMPTS}` +
        `${last ? '. Falling back to local hint.' : '. Retrying.'}`,
        lookupErr,
      );
      if (timedOut) break;
    }
  }

  if (existing) {
    for (const snap of existing.docs) {
      const data = snap.data();
      if (data.isDemo) continue;
      // A purged wedding has had its guest data deleted; opening it would show
      // the couple an empty shell. Filtered here rather than in the query for
      // the reason above.
      if (data.status === 'purged') continue;
      return { weddingId: snap.id, outcome: 'existing' };
    }
  } else if (resumeHint) {
    return { weddingId: resumeHint, outcome: 'resume-hint' };
  }

  // Nothing of theirs to open. Bounded so a dropped connection surfaces an
  // error instead of spinning forever; if the write does land later, the lookup
  // above picks it up next time and routes them into it.
  const weddingId = generateWeddingId();
  await withTimeout(
    setDoc(doc(db, 'weddings', weddingId), {
      ownerUids: [user.uid],
      status: 'active',
      weddingDate: null,
      editLockAt: null,
      archivedAt: null,
      purgeAt: null,
      licenseLockedAt: null,
      // On, so the 30-day deletion the privacy policy promises actually
      // happens. The stage timestamps stay null until the couple sets a wedding
      // date — AdminPage fills them in then, and the job skips undated
      // weddings, so nothing is deleted on a clock the couple was never shown.
      lifecycleEnabled: true,
      // Which version of each document was on screen when they pressed the
      // button — a bare timestamp cannot answer "agreed to what?".
      consent: consentRecord(serverTimestamp()),
      createdAt: serverTimestamp(),
    }),
    CREATE_TIMEOUT_MS,
  );
  return { weddingId, outcome: 'created' };
}

/** Message for a failure from resolveOrCreateWedding, in the couple's terms. */
export const describeClaimError = (err) =>
  err?.message === 'timeout'
    ? 'Could not reach the server. Check your connection and try again.'
    : err?.code === 'permission-denied'
      ? 'Your account is not allowed to start a wedding. Please contact us.'
      : 'Could not open your wedding. Please try again.';
